/**
 * Shared SEDCAR overbook / class-size variance dialogs.
 * Used by classroom-side enroll and student-record class assignment.
 */
import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

export const WAIVER_STATUS_LABELS = {
  draft: 'Draft',
  filed: 'Filed — awaiting decision',
  approved: 'Approved',
  denied: 'Denied',
  expired: 'Expired',
};

export const waiverStatusChip = (status) => {
  const colors = { draft: 'default', filed: 'info', approved: 'success', denied: 'error', expired: 'default' };
  return <Chip label={WAIVER_STATUS_LABELS[status] || status} size="small" color={colors[status] || 'default'} />;
};

export const emptyWaiverForm = () => ({
  student: '',
  status: 'draft',
  filed_date: '',
  decision_date: '',
  sedcar_reference_number: '',
  notes: '',
});

/** Pull the capacity_exceeded payload from a student PATCH 400 (nested or top-level). */
export const extractCapacityExceeded = (data) => {
  if (!data || typeof data !== 'object') return null;
  if (data.error === 'capacity_exceeded') return data;
  const nested = data.class_num;
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && nested.error === 'capacity_exceeded') {
    return nested;
  }
  return null;
};

export function CapacityExceededDialog({
  open,
  payload,
  canEdit,
  onClose,
  onFileWaiver,
  onUpdateWaiver,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Classroom at capacity</DialogTitle>
      <DialogContent dividers>
        {payload && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>{payload.classroomName}</strong> is at its{' '}
              {(payload.effective_capacity ?? payload.licensed_max) > payload.licensed_max
                ? 'SEDCAR-approved' : 'licensed'} capacity:{' '}
              {payload.class_ratio} — {payload.effective_capacity ?? payload.licensed_max} students.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              To enroll <strong>{payload.studentName}</strong>, an approved SEDCAR waiver must be on file
              for this student, classroom, and session.
            </Typography>
            <Typography variant="body2">
              Current waiver status:{' '}
              {payload.waiver_status
                ? waiverStatusChip(payload.waiver_status)
                : 'None on file'}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {payload?.waiver_status && payload.waiver_status !== 'approved' && canEdit && (
          <Button onClick={() => onUpdateWaiver(String(payload.student_id))}>Update Waiver Status</Button>
        )}
        {canEdit && (
          <Button variant="contained" onClick={() => onFileWaiver(String(payload?.student_id))}>
            File a Waiver
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function WaiverFormDialog({
  open,
  editId,
  form,
  onFormChange,
  studentOptions,
  saving,
  onClose,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{editId ? 'Edit waiver' : 'Add waiver'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Student</InputLabel>
              <Select
                label="Student"
                value={form.student || ''}
                onChange={(e) => onFormChange({ ...form, student: e.target.value })}
              >
                {studentOptions.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={(e) => onFormChange({ ...form, status: e.target.value })}
              >
                {Object.entries(WAIVER_STATUS_LABELS).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Filed date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.filed_date}
              onChange={(e) => onFormChange({ ...form, filed_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Decision date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.decision_date}
              onChange={(e) => onFormChange({ ...form, decision_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="SEDCAR reference #"
              value={form.sedcar_reference_number}
              onChange={(e) => onFormChange({ ...form, sedcar_reference_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={(e) => onFormChange({ ...form, notes: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !saving && onClose()}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={saving || !form.student}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
